import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";
import Notification from "../utility/Notification";
import ConfirmDeleteMenuModal from "./ConfirmDeleteMenuModal";
import ConfirmDeleteCategoryModal from "./ConfirmDeleteMenuModal";
import {
  addCategory,
  deleteCategory,
  addAllergens,
  deleteAllergens,
  addMenu,
  deleteMenu,
} from "../utility/EndPointGoogleFirebase";
import { MdDeleteForever } from "react-icons/md";
import { FaChevronDown, FaPlus, FaSave, FaEdit } from "react-icons/fa";

// ── Input / Textarea styled ──────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    {label && (
      <label style={{ fontSize: 12, fontWeight: 500, color: "#374151" }}>{label}</label>
    )}
    {children}
  </div>
);

const inputStyle = (filled) => ({
  width: "100%", padding: "10px 12px", borderRadius: 8, fontFamily: "inherit",
  fontSize: 13, color: "#111827", outline: "none",
  border: `1px solid ${filled ? "#a5b4fc" : "#e5e7eb"}`,
  background: filled ? "#eef2ff" : "#fff",
  transition: "border-color 0.15s, background 0.15s",
});

// ── Accordion section ────────────────────────────────────────────────
const Section = ({ title, accentColor, isOpen, onToggle, children }) => (
  <div style={{
    background: "#fff", borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflow: "hidden",
    borderTop: `3px solid ${accentColor}`,
  }}>
    <div
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 16px", cursor: "pointer", userSelect: "none",
        WebkitTapHighlightColor: "transparent",
        borderBottom: isOpen ? "1px solid #f3f4f6" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 8, height: 8, borderRadius: "50%",
          background: accentColor, flexShrink: 0,
        }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{title}</span>
      </div>
      <div style={{
        color: "#9ca3af", fontSize: 12,
        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.25s ease",
      }}>
        <FaChevronDown />
      </div>
    </div>
    {isOpen && (
      <div style={{ padding: "16px" }}>{children}</div>
    )}
  </div>
);

// ── Tag chip (categoria / allergene) ────────────────────────────────
const Chip = ({ label, onDelete }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 6,
    padding: "5px 10px", borderRadius: 20,
    background: "#fee2e2", border: "1px solid #fecaca",
    fontSize: 12, fontWeight: 500, color: "#991b1b",
  }}>
    <span>{label}</span>
    <button
      onClick={onDelete}
      style={{
        background: "none", border: "none", padding: 0, cursor: "pointer",
        color: "#dc2626", display: "flex", alignItems: "center",
        lineHeight: 1,
      }}
    >
      <MdDeleteForever size={16} />
    </button>
  </div>
);

// ── Button ───────────────────────────────────────────────────────────
const Btn = ({ onClick, color = "#4f46e5", children, fullWidth, danger }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      padding: "9px 16px", borderRadius: 8, border: "none",
      background: danger ? "#dc2626" : color,
      color: "#fff", fontSize: 13, fontWeight: 500,
      cursor: "pointer", fontFamily: "inherit",
      width: fullWidth ? "100%" : "auto",
      transition: "opacity 0.15s",
      WebkitTapHighlightColor: "transparent",
    }}
    onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
    onMouseLeave={e => e.currentTarget.style.opacity = "1"}
  >
    {children}
  </button>
);

// ── Main component ───────────────────────────────────────────────────
const CreaMenu = () => {
  const [categorie, setCategorie]               = useState([]);
  const [allergeni, setAllergeni]               = useState([]);
  const [menuItems, setMenuItems]               = useState([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [nuovaCategoria, setNuovaCategoria]     = useState("");
  const [nuovoAllergene, setNuovoAllergene]     = useState("");
  const [menuItem, setMenuItem]                 = useState({ categoria: "", nome: "", ingredienti: "", allergeni: [], prezzo: "", note: "" });
  const [loadingMessage, setLoadingMessage]     = useState("");
  const [loading, setLoading]                   = useState(false);
  const [openAccordion, setOpenAccordion]       = useState(null);
  const [notification, setNotification]         = useState(null);
  const [isConfirmDeleteMenuModalOpen, setConfirmDeleteMenuModalOpen]     = useState(false);
  const [isConfirmDeleteCategoryModalOpen, setConfirmDeleteCategoryModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const db = getFirestore();

  const resetState = () => {
    setNuovaCategoria("");
    setNuovoAllergene("");
    setMenuItem({ categoria: "", nome: "", ingredienti: "", allergeni: [], prezzo: "", note: "" });
    setFilteredMenuItems([]);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingMessage("Caricamento in corso...");
      setLoading(true);
      try {
        const options = { source: "server" };
        const [catSnap, allerSnap, menuSnap] = await Promise.all([
          getDocs(collection(db, "categorie"), options),
          getDocs(collection(db, "allergeni"), options),
          getDocs(collection(db, "menu"), options),
        ]);
        setCategorie(catSnap.docs.map(d => ({ id: d.id, tipo: d.data().tipo.trim() })));
        setAllergeni(allerSnap.docs.map(d => ({ id: d.id, nome: d.data().nome.trim() })));
        setMenuItems(menuSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        setNotification({ type: "error", message: "Errore nel caricamento dei dati." });
      }
      setLoading(false);
    };
    fetchData();
  }, [db]);

  const aggiungiCategoria = async () => {
    if (!nuovaCategoria.trim()) { setNotification({ type: "error", message: "Inserisci una categoria!" }); return; }
    setLoadingMessage("Salvataggio..."); setLoading(true);
    try {
      const newCat = await addCategory(nuovaCategoria.trim());
      setCategorie([...categorie, { id: newCat.id, tipo: nuovaCategoria.trim() }]);
      setNuovaCategoria("");
      setNotification({ type: "success", message: "Categoria aggiunta!" });
    } catch { setNotification({ type: "error", message: "Errore aggiunta categoria." }); }
    setLoading(false);
  };

  const rimuoviCategoria = async (id) => {
    setLoadingMessage("Eliminazione..."); setLoading(true);
    try {
      await deleteCategory(id, categoryToDelete?.tipo);
      setCategorie(categorie.filter(c => c.id !== id));
      resetState();
      setNotification({ type: "success", message: "Categoria eliminata!" });
    } catch { setNotification({ type: "error", message: "Errore eliminazione categoria." }); }
    setLoading(false);
    setConfirmDeleteCategoryModalOpen(false);
  };

  const aggiungiAllergene = async () => {
    if (!nuovoAllergene.trim()) { setNotification({ type: "error", message: "Inserisci un allergene!" }); return; }
    setLoadingMessage("Salvataggio..."); setLoading(true);
    try {
      const newAll = await addAllergens(nuovoAllergene.trim());
      setAllergeni([...allergeni, { id: newAll.id, nome: nuovoAllergene.trim() }]);
      setNuovoAllergene("");
      setNotification({ type: "success", message: "Allergene aggiunto!" });
    } catch { setNotification({ type: "error", message: "Errore aggiunta allergene." }); }
    setLoading(false);
  };

  const rimuoviAllergene = async (id) => {
    setLoadingMessage("Eliminazione..."); setLoading(true);
    try {
      await deleteAllergens(id);
      setAllergeni(allergeni.filter(a => a.id !== id));
      resetState();
      setNotification({ type: "success", message: "Allergene eliminato!" });
    } catch { setNotification({ type: "error", message: "Errore eliminazione allergene." }); }
    setLoading(false);
  };

  const creaMenuItem = async () => {
    if (!menuItem.categoria || !menuItem.nome.trim() || !menuItem.prezzo.trim()) {
      setNotification({ type: "error", message: "Categoria, nome e prezzo sono obbligatori!" }); return;
    }
    setLoadingMessage("Salvataggio..."); setLoading(true);
    try {
      const newItem = await addMenu({ ...menuItem, nome: menuItem.nome.trim(), prezzo: parseFloat(menuItem.prezzo.trim()) });
      setMenuItems([...menuItems, { id: newItem.id, ...menuItem }]);
      resetState();
      setNotification({ type: "success", message: "Voce menu aggiunta!" });
    } catch { setNotification({ type: "error", message: "Errore aggiunta menu." }); }
    setLoading(false);
  };

  const handleDeleteMenuItem = async () => {
    setLoadingMessage("Eliminazione..."); setLoading(true);
    try {
      await deleteMenu(menuItem.id);
      setMenuItems(menuItems.filter(i => i.id !== menuItem.id));
      resetState();
      setNotification({ type: "success", message: "Voce eliminata!" });
    } catch { setNotification({ type: "error", message: "Errore eliminazione voce." }); }
    setLoading(false);
    setConfirmDeleteMenuModalOpen(false);
  };

  const handleSaveModifica = async () => {
    setLoadingMessage("Salvataggio..."); setLoading(true);
    try {
      const docRef = doc(db, "menu", menuItem.id);
      await updateDoc(docRef, {
        nome: menuItem.nome, ingredienti: menuItem.ingredienti,
        allergeni: menuItem.allergeni, prezzo: parseFloat(menuItem.prezzo),
        note: menuItem.note, visibile: menuItem.visibile !== false,
      });
      setMenuItems(prev => prev.map(i => i.id === menuItem.id ? menuItem : i));
      setNotification({ type: "success", message: "Menu aggiornato!" });
    } catch { setNotification({ type: "error", message: "Errore aggiornamento menu." }); }
    setLoading(false);
  };

  const toggleAccordion = (key) => { setOpenAccordion(prev => prev === key ? null : key); resetState(); };
  const toggleAllergene = (nome) => {
    setMenuItem(prev => ({
      ...prev,
      allergeni: prev.allergeni.includes(nome) ? prev.allergeni.filter(a => a !== nome) : [...prev.allergeni, nome],
    }));
  };

  // Shared form fields for create/edit
  const MenuForm = ({ onSave, onDelete, showVisibile }) => {
    const isVisibile = menuItem.visibile !== false; // default true se non impostato
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Nome">
            <input style={inputStyle(!!menuItem.nome)} value={menuItem.nome}
              placeholder="Nome voce" onChange={e => setMenuItem({ ...menuItem, nome: e.target.value })} />
          </Field>
          <Field label="Prezzo (€)">
            <input style={inputStyle(!!menuItem.prezzo)} value={menuItem.prezzo}
              placeholder="0.00" onChange={e => setMenuItem({ ...menuItem, prezzo: e.target.value })} />
          </Field>
        </div>
        <Field label="Ingredienti">
          <textarea style={{ ...inputStyle(!!menuItem.ingredienti), resize: "vertical", minHeight: 72 }}
            value={menuItem.ingredienti} placeholder="Ingredienti..."
            onChange={e => setMenuItem({ ...menuItem, ingredienti: e.target.value })} />
        </Field>
        <Field label="Note">
          <textarea style={{ ...inputStyle(!!menuItem.note), resize: "vertical", minHeight: 60 }}
            value={menuItem.note} placeholder="Note..."
            onChange={e => setMenuItem({ ...menuItem, note: e.target.value })} />
        </Field>

        {/* Allergeni checkboxes */}
        <Field label="Allergeni">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 }}>
            {allergeni.map(a => (
              <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
                <input type="checkbox" checked={menuItem.allergeni.includes(a.nome)}
                  onChange={() => toggleAllergene(a.nome)}
                  style={{ accentColor: "#4f46e5", width: 15, height: 15 }} />
                {a.nome}
              </label>
            ))}
          </div>
        </Field>

        {/* Toggle visibilità — solo in modifica */}
        {showVisibile && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 14px", borderRadius: 10,
            background: isVisibile ? "#f0fdf4" : "#fff7ed",
            border: `1px solid ${isVisibile ? "#bbf7d0" : "#fed7aa"}`,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                Visibile nel menu
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                {isVisibile ? "I clienti possono vedere questa voce" : "Voce nascosta ai clienti"}
              </div>
            </div>
            {/* Toggle switch */}
            <div
              onClick={() => setMenuItem({ ...menuItem, visibile: !isVisibile })}
              style={{
                width: 44, height: 24, borderRadius: 12, cursor: "pointer",
                background: isVisibile ? "#22c55e" : "#d1d5db",
                position: "relative", transition: "background 0.2s", flexShrink: 0,
              }}
            >
              <div style={{
                position: "absolute", top: 3,
                left: isVisibile ? 23 : 3,
                width: 18, height: 18, borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }} />
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <Btn onClick={onSave} fullWidth><FaSave style={{ fontSize: 12 }} /> Salva</Btn>
          {onDelete && <Btn onClick={onDelete} danger fullWidth><MdDeleteForever size={15} /> Elimina voce</Btn>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", opacity: loading ? 0.7 : 1, pointerEvents: loading ? "none" : "auto" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        .cm-loader-ring {
          width: 36px; height: 36px;
          border: 2px solid #e5e7eb; border-top-color: #4f46e5;
          border-radius: 50%; animation: cmSpin 0.8s linear infinite;
        }
        @keyframes cmSpin { to { transform: rotate(360deg); } }
        .cm-wrapper {
          max-width: 800px; margin: 0 auto; padding: 16px 12px;
          padding-left: max(12px, env(safe-area-inset-left));
          padding-right: max(12px, env(safe-area-inset-right));
          padding-bottom: max(32px, env(safe-area-inset-bottom));
          display: flex; flex-direction: column; gap: 14px;
        }
        @media (min-width: 480px)  { .cm-wrapper { padding: 20px 16px; } }
        @media (min-width: 768px)  { .cm-wrapper { padding: 28px 24px; } }
        @media (min-width: 1024px) { .cm-wrapper { padding: 36px 32px; } }
        .page-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(22px, 5vw, 34px);
          font-weight: 400; color: #111827; line-height: 1.2;
        }
        select.cm-select {
          width: 100%; padding: 10px 12px; border-radius: 8;
          border: 1px solid #e5e7eb; background: #fff;
          font-size: 13px; font-family: inherit; color: #111827;
          outline: none; cursor: pointer;
        }
        select.cm-select:focus { border-color: #a5b4fc; }
      `}</style>

      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14,
          background: "rgba(249,250,251,0.88)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        }}>
          <div className="cm-loader-ring" />
          <p style={{ fontSize: 12, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {loadingMessage}
          </p>
        </div>
      )}

      <ConfirmDeleteMenuModal
        isOpen={isConfirmDeleteMenuModalOpen}
        onConfirm={handleDeleteMenuItem}
        onCancel={() => setConfirmDeleteMenuModalOpen(false)}
        clientName={menuItem.nome}
      />
      <ConfirmDeleteCategoryModal
        isOpen={isConfirmDeleteCategoryModalOpen}
        onConfirm={() => rimuoviCategoria(categoryToDelete?.id)}
        onCancel={() => setConfirmDeleteCategoryModalOpen(false)}
        clientName={categoryToDelete?.tipo}
      />
      <Notification notification={notification} onClose={() => setNotification(null)} />

      <div className="cm-wrapper">

        {/* Page header */}
        <div style={{ textAlign: "center", paddingBottom: 20, borderBottom: "1px solid #e5e7eb" }}>
          <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 6 }}>
            Configurazione
          </p>
          <h1 className="page-title">Gestione Menu</h1>
          <div style={{ width: 28, height: 2, background: "#4f46e5", borderRadius: 2, margin: "10px auto 0" }} />
        </div>

        {/* ── Categorie ── */}
        <Section title="Gestione Categorie" accentColor="#4f46e5"
          isOpen={openAccordion === "categorie"} onToggle={() => toggleAccordion("categorie")}>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <input
              style={{ ...inputStyle(!!nuovaCategoria), flex: 1 }}
              placeholder="Nuova categoria..."
              value={nuovaCategoria}
              onChange={e => setNuovaCategoria(e.target.value)}
              onKeyDown={e => e.key === "Enter" && aggiungiCategoria()}
            />
            <Btn onClick={aggiungiCategoria}><FaPlus style={{ fontSize: 11 }} /> Aggiungi</Btn>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {categorie.length === 0
              ? <p style={{ fontSize: 12, color: "#9ca3af" }}>Nessuna categoria.</p>
              : categorie.map(cat => (
                <Chip key={cat.id} label={cat.tipo} onDelete={() => { setCategoryToDelete(cat); setConfirmDeleteCategoryModalOpen(true); }} />
              ))}
          </div>
        </Section>

        {/* ── Allergeni ── */}
        <Section title="Gestione Allergeni" accentColor="#f59e0b"
          isOpen={openAccordion === "allergeni"} onToggle={() => toggleAccordion("allergeni")}>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <input
              style={{ ...inputStyle(!!nuovoAllergene), flex: 1 }}
              placeholder="Nuovo allergene..."
              value={nuovoAllergene}
              onChange={e => setNuovoAllergene(e.target.value)}
              onKeyDown={e => e.key === "Enter" && aggiungiAllergene()}
            />
            <Btn onClick={aggiungiAllergene} color="#f59e0b"><FaPlus style={{ fontSize: 11 }} /> Aggiungi</Btn>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {allergeni.length === 0
              ? <p style={{ fontSize: 12, color: "#9ca3af" }}>Nessun allergene.</p>
              : allergeni.map(a => (
                <Chip key={a.id} label={a.nome} onDelete={() => rimuoviAllergene(a.id)} />
              ))}
          </div>
        </Section>

        {/* ── Crea voce menu ── */}
        <Section title="Crea Voce Menu" accentColor="#22c55e"
          isOpen={openAccordion === "menu"} onToggle={() => toggleAccordion("menu")}>
          <Field label="Categoria">
            <select className="cm-select" style={inputStyle(!!menuItem.categoria)}
              value={menuItem.categoria}
              onChange={e => setMenuItem({ ...menuItem, categoria: e.target.value })}>
              <option value="">Seleziona categoria...</option>
              {categorie.map(cat => <option key={cat.id} value={cat.tipo}>{cat.tipo}</option>)}
            </select>
          </Field>
          <div style={{ marginTop: 12 }}>
            <MenuForm onSave={creaMenuItem} showVisibile={false} />
          </div>
        </Section>

        {/* ── Modifica voce menu ── */}
        <Section title="Modifica Voce Menu" accentColor="#3b82f6"
          isOpen={openAccordion === "modificaMenu"} onToggle={() => toggleAccordion("modificaMenu")}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="Categoria">
              <select className="cm-select" style={inputStyle(!!menuItem.categoria)}
                value={menuItem.categoria}
                onChange={e => {
                  const cat = e.target.value;
                  setMenuItem({ ...menuItem, categoria: cat });
                  setFilteredMenuItems(menuItems.filter(i => i.categoria === cat));
                }}>
                <option value="">Seleziona categoria...</option>
                {categorie.map(cat => <option key={cat.id} value={cat.tipo}>{cat.tipo}</option>)}
              </select>
            </Field>

            {filteredMenuItems.length > 0 && (
              <Field label="Voce menu">
                <select className="cm-select" style={inputStyle(!!menuItem.id)}
                  value={menuItem.id || ""}
                  onChange={e => setMenuItem(filteredMenuItems.find(i => i.id === e.target.value) || {})}>
                  <option value="">Seleziona voce...</option>
                  {filteredMenuItems.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.visibile === false ? "🚫 " : ""}{item.nome}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {menuItem.id && (
              <MenuForm
                onSave={handleSaveModifica}
                onDelete={() => setConfirmDeleteMenuModalOpen(true)}
                showVisibile={true}
              />
            )}
          </div>
        </Section>

      </div>
    </div>
  );
};

export default CreaMenu;